import { BlobDto, ImageApi } from "../Repository/eventbookyapi";
import { GetApiConfiguration } from "./ServiceConfiguration";

export abstract class DocumentService {
    public static GetImage = async (url: string) => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include' as RequestCredentials
        };
        return await fetch(process.env.REACT_APP_API_URL + '/Image/downloadFile?bloburl=' + url, requestOptions);
    };

    public static GetFile = async (url: string) => {
        return new ImageApi(GetApiConfiguration()).apiImageDownloadFileGetRaw({
            bloburl: url
        });
    };

    public static GetBannerImages = async () => {
        return new ImageApi(GetApiConfiguration()).apiImageGetimagesGet();
    };

    public static GetPublicImages = async () => {
        return new ImageApi(GetApiConfiguration()).apiImageGetCompanyPublicImagesGet();
    };

    public static UploadPublicImage = async () => {
        return new ImageApi(GetApiConfiguration()).apiImageUploadPublicImagePost();
    };


    // Names of the events using a picture archive image as banner. Plain fetch until the generated
    // client is refreshed (generate-api).
    public static GetImageUsage = async (blobUri: string): Promise<string[]> => {
        const response = await fetch(process.env.REACT_APP_API_URL + '/Image/imageUsage?bloburl=' + encodeURIComponent(blobUri), {
            credentials: 'include' as RequestCredentials
        });
        return response.ok ? await response.json() : [];
    };

    // Uploads a file to the picture archive and returns its relative path - same endpoint the
    // UploadImage component posts to, for files made in the browser (e.g. a cropped banner).
    public static UploadToPictureArchive = async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append("file", file);
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/api/Image/uploadPublicImage', {
            method: 'POST',
            body: formData,
            credentials: 'include' as RequestCredentials
        });
        if (!response.ok) {
            throw new Error(await response.text());
        }
        return (await response.json()).blob.relativeUri;
    };

    // Fetches an image through the authenticated API as a same-origin object URL, so it can be drawn
    // to a canvas (cropped) without the canvas being tainted. Revoke it with URL.revokeObjectURL.
    public static GetImageObjectUrl = async (relativeUri: string): Promise<string> => {
        const response = await fetch(process.env.REACT_APP_API_URL + '/Image/downloadFile?bloburl=' + encodeURIComponent(relativeUri), {
            credentials: 'include' as RequestCredentials
        });
        if (!response.ok) {
            throw new Error(await response.text());
        }
        return URL.createObjectURL(await response.blob());
    };

    // The picture archive (Bildarkiv) is the company's single store for its own images - emails,
    // templates and event banners.
    public static PictureArchiveMaxSizeMB = 20;

    public static GetPictureArchiveSizeMB = (images: BlobDto[]) => {
        return images.reduce((total, image) => total + (image.size || 0), 0) / (1024 * 1024);
    };

    public static IsPictureArchiveFull = (images: BlobDto[]) => {
        return DocumentService.GetPictureArchiveSizeMB(images) >= DocumentService.PictureArchiveMaxSizeMB;
    };

    public static GetFileNames = (images: BlobDto[]) => {
        return images.map(image => image.name!.split("/").pop()!);
    };

    public static GetBackupFiles = async () => {
        return new ImageApi(GetApiConfiguration()).apiImageGetbackupsGet();
    };

    public static DeleteBannerImage = async (blobUri: string) => {
        return new ImageApi(GetApiConfiguration()).apiImageDeleteImageDelete({
            bloburl: blobUri
        });
    };

    public static DeletePublicImage = async (blobUri: string) => {
        return new ImageApi(GetApiConfiguration()).apiImageDeletePublicImageDelete({
            bloburl: blobUri
        });
    };

    public static getDownloadUrl = (dto: BlobDto) => {
        return process.env.REACT_APP_API_URL + '/Image/downloadFile?bloburl=' + dto.relativeUri;
    }
}